/**
 * The web implementation of `expo-router`, wired by a Vite alias.
 *
 * Shared modules (notably src/store/buildRunStore.ts) navigate imperatively through
 * `router`, using Expo Router's group syntax: `/(main)/project/abc`. Groups in
 * parentheses organise files and never appear in a URL, so they are stripped here — the
 * one translation that matters, since a build that finishes and lands on a 404 is the
 * whole feature failing at its last step.
 */
type NavigateFn = ((to: string, opts: { replace: boolean }) => void) & ((delta: number) => void);

let navigate: NavigateFn | null = null;
let pending: Array<() => void> = [];

/** Strip `(group)` segments and collapse a bare tabs root to `/`. */
export function toWebPath(expoPath: string): string {
  const [rawPath, query] = expoPath.split('?');
  const segments = rawPath
    .split('/')
    .filter((s) => s.length > 0 && !(s.startsWith('(') && s.endsWith(')')));
  // `index` is Expo Router's name for a directory's default route; the web has no such file.
  if (segments[segments.length - 1] === 'index') segments.pop();
  const path = segments.length === 0 ? '/' : `/${segments.join('/')}`;
  return query ? `${path}?${query}` : path;
}

function run(fn: () => void): void {
  if (navigate) fn();
  else pending.push(fn);
}

export const router = {
  push: (href: string): void => {
    const to = toWebPath(href);
    run(() => navigate!(to, { replace: false }));
  },
  replace: (href: string): void => {
    const to = toWebPath(href);
    run(() => navigate!(to, { replace: true }));
  },
  back: (): void => {
    run(() => (navigate as unknown as (d: number) => void)(-1));
  },
  /** Called once by AppShell with React Router's navigate; null in tests to reset. */
  setNavigate: (fn: NavigateFn | null): void => {
    navigate = fn;
    if (fn) {
      const queued = pending;
      pending = [];
      queued.forEach((f) => f());
    }
  },
};

/** Shared code type-imports these; they are never called on the web. */
export type Href = string;
export const useLocalSearchParams = (): Record<string, string> => {
  throw new Error('useLocalSearchParams is not available on the web — use React Router useParams.');
};
